import type { GroupIterations } from "@/board/location/PlanningBoardLocation";
import { statusClasses } from "@/model/baseTypes";
import type { StatusClassDistribution } from "@/model/status";

export const emptyDistribution = () => new Map() as StatusClassDistribution;

export function multiIterationStatusDistribution(
  groups: GroupIterations[],
  iterationId: number,
): StatusClassDistribution {
  const statusDistributions = groups.flatMap(
    (group) => group.iterations[iterationId]?.statusDistribution || [],
  );
  return sumOfStatusDistributions(statusDistributions);
}

export function groupStatusDistribution(group: GroupIterations): StatusClassDistribution {
  const statusDistributions = (group.iterations || []).flatMap(
    (iteration) => iteration.statusDistribution || [],
  );
  return sumOfStatusDistributions(statusDistributions);
}

export function mergeStatusDistribution(
  target: StatusClassDistribution,
  source?: StatusClassDistribution,
) {
  source?.forEach((value, statusClass) => {
    if (!target.has(statusClass)) {
      target.set(statusClass, { points: 0, value: 0 });
    }
    const v = target.get(statusClass)!;
    v.points += value.points;
    v.value += value.value;
  });
}

function initData(): StatusClassDistribution {
  const data: StatusClassDistribution = new Map();
  // set all status classes to ensure they are ordered correctly
  for (const statusClass of statusClasses) {
    data.set(statusClass, { points: 0, value: 0 });
  }
  return data;
}

function removeEmptyStatusClasses(data: StatusClassDistribution) {
  for (const [statusClass, value] of data) {
    if (value.value === 0) {
      data.delete(statusClass);
    }
  }
  return data;
}

function sumOfStatusDistributions(statusDistributions: StatusClassDistribution[]) {
  const sum = statusDistributions.reduce((acc, curr) => {
    for (const [statusClass, value] of curr.entries()) {
      const existingValue = acc.get(statusClass) || { points: 0, value: 0 };
      existingValue.points += value.points;
      existingValue.value += value.value;
      acc.set(statusClass, existingValue);
    }
    return acc;
  }, initData());

  return removeEmptyStatusClasses(sum);
}
